import { useCallback } from "react";
import useSWR from "swr";
import { Paginated, PaginationQuery } from "../types";
import { buildUrl, mutateCache, useApi } from "./api";
import { NOTIFICATIONS_URL } from "./useNotifications";
import { CustomError } from "./errors";
import { CalculationSharingInvite } from "./types/calculationSharingInvites";

export const useCalculationSharingInvitesApi = () => {
  const api = useApi();

  const getCalculationsUrl = useCallback((isAdmin?: boolean) => `${isAdmin ? "/admin" : ""}/calculations`, []);

  const getBaseUrl = useCallback(
    (calculationId: string, isAdmin?: boolean) => `${getCalculationsUrl(isAdmin)}/${calculationId}/sharing_invites`,
    [getCalculationsUrl],
  );

  const mutateCalculationSharingInvites = useCallback(
    (calculationId: string, isAdmin?: boolean) => {
      mutateCache([getCalculationsUrl(isAdmin), getBaseUrl(calculationId, isAdmin)]);
    },
    [getCalculationsUrl, getBaseUrl],
  );

  const useCalculationSharingInvites = useCallback(
    (calculationId: string, query: PaginationQuery, signal: AbortSignal, isAdmin?: boolean) => {
      const url = buildUrl(getBaseUrl(calculationId, isAdmin), query);
      return useSWR<Paginated<CalculationSharingInvite>, CustomError, string>(url, (url) =>
        api.get<Paginated<CalculationSharingInvite>>({ url, signal }),
      );
    },
    [api, getBaseUrl],
  );

  const resendCalculationSharingInvite = useCallback(
    async (calculationId: string, inviteId: string, isAdmin?: boolean) => {
      const url = `${getBaseUrl(calculationId, isAdmin)}/${inviteId}/re_send`;
      await api.post(url, {});
      mutateCalculationSharingInvites(calculationId, isAdmin);
    },
    [api, getBaseUrl, mutateCalculationSharingInvites],
  );

  const cancelCalculationSharingInvite = useCallback(
    async (calculationId: string, inviteId: string, isAdmin?: boolean): Promise<void> => {
      const url = `${getBaseUrl(calculationId, isAdmin)}/${inviteId}/cancel`;
      await api.put(url, {});
      mutateCalculationSharingInvites(calculationId, isAdmin);
    },
    [api, getBaseUrl, mutateCalculationSharingInvites],
  );

  const deleteCalculationSharingInvite = useCallback(
    async (calculationId: string, inviteId: string, isAdmin?: boolean): Promise<void> => {
      const url = `${getBaseUrl(calculationId, isAdmin)}/${inviteId}/delete`;
      await api.del(url);
      mutateCalculationSharingInvites(calculationId, isAdmin);
    },
    [api, getBaseUrl, mutateCalculationSharingInvites],
  );

  const acceptSharingInvite = useCallback(
    async (token: string): Promise<{ calculationId: string }> => {
      const url = `/calculations/sharing_invites/accept`;
      const result = await api.post<{ calculationId: string }, { token: string }>(url, { token });
      const { calculationId } = result;
      mutateCache([
        `/calculations`,
        `/calculations/${calculationId}`,
        `/calculations/${calculationId}/sharing`,
        `/calculations/${calculationId}/sharing_invites`,
        NOTIFICATIONS_URL,
      ]);
      return result;
    },
    [api],
  );

  return {
    useCalculationSharingInvites,
    resendCalculationSharingInvite,
    cancelCalculationSharingInvite,
    acceptSharingInvite,
    deleteCalculationSharingInvite,
  };
};
